import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ButtonHoverAnimation from "./button/ButtonHoverAnimation";
import SvgSpinner from "./svgspinner";
import {
  useAccount,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { useNftsForUser } from "./balances";
import { parseAbi } from "viem";

const brainCA = process.env.REACT_APP_BRAIN_ERC721_CA;

const BRAIN_NFT_ABI = parseAbi([
  "function updateBrainMetadata(uint256 tokenId, string name, string ticker, string metadataUrl, string imageUrl)",
]);

const Metadata = () => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [brainName, setBrainName] = useState("");
  const [brainTicker, setBrainTicker] = useState("");
  const [brainDomain, setBrainDomain] = useState("");
  const [brainImage, setBrainImage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { address } = useAccount();
  const { ownedBrains, loading } = useNftsForUser(address);

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisibility = () => setIsVisible(!isVisible);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 575.98);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 575.98);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    data: metadataHash,
    error: metadataError,
    isPending: isMetadataPending,
    writeContract: writeMetadataContract,
  } = useWriteContract();

  const {
    isLoading: isWaitingForTransaction,
    isSuccess: isTransactionSuccess,
  } = useWaitForTransactionReceipt({
    hash: metadataHash,
  });

  const getPhaseContent = () => {
    switch (currentPhase) {
      case 1:
        return {
          headerTitle: "1: Select Brain",
          inputLabel: "Select Brain",
          isDropdown: true,
          value: selectedTokenId,
          onChange: (e) => setSelectedTokenId(e.target.value),
          buttonText: selectedTokenId ? "Next" : "Select a Brain",
          isDisabled: !selectedTokenId || loading,
        };
      case 2:
        return {
          headerTitle: "2: Name your Brain",
          inputLabel: "Enter name",
          placeholder: "Enter name",
          value: brainName,
          onChange: (e) => setBrainName(e.target.value),
          buttonText: brainName ? "Next" : "Enter a name",
          isDisabled: !brainName,
        };
      case 3:
        return {
          headerTitle: "3: Give your Brain a ticker",
          inputLabel: "Enter ticker",
          placeholder: "Enter ticker",
          value: brainTicker,
          onChange: (e) => setBrainTicker(e.target.value),
          buttonText: brainTicker ? "Next" : "Enter a ticker",
          isDisabled: !brainTicker,
        };
      case 4:
        return {
          headerTitle: "4: Add a web domain",
          inputLabel: "Enter domain",
          placeholder: "Enter website url",
          value: brainDomain,
          onChange: (e) => setBrainDomain(e.target.value),
          buttonText: brainDomain ? "Next" : "Enter a domain",
          isDisabled: !brainDomain,
        };
      case 5:
        return {
          headerTitle: "5: Add a logo/image",
          inputLabel: "Enter image url",
          placeholder: "Enter image URL",
          value: brainImage,
          onChange: (e) => setBrainImage(e.target.value),
          buttonText: brainImage ? "Next" : "Enter an image URL",
          isDisabled: !brainImage,
        };
      case 6:
        return {
          headerTitle: "Preview",
          isPreview: true,
          buttonText:
            isSubmitting || isMetadataPending ? <SvgSpinner /> : "Confirm",
          isDisabled: isSubmitting || isMetadataPending,
        };
      case 7:
        return {
          headerTitle: isWaitingForTransaction
            ? "Updating Metadata..."
            : "Metadata Update Success!",
          isPreview: true,
          buttonText: isWaitingForTransaction ? (
            <SvgSpinner />
          ) : (
            "View Inventory"
          ),
          isDisabled: isWaitingForTransaction,
        };
      default:
        return {};
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();

    if (currentPhase === 6) {
      setIsSubmitting(true);
      setError(null);
      try {
        await writeMetadataContract({
          address: brainCA,
          abi: BRAIN_NFT_ABI,
          functionName: "updateBrainMetadata",
          args: [
            selectedTokenId,
            brainName,
            brainTicker,
            brainDomain,
            brainImage,
          ],
        });
      } catch (error) {
        console.error("Contract write failed", error);
        setError("Failed to update metadata. Please try again.");
        setIsSubmitting(false);
      }
    } else if (currentPhase === 7 && !isWaitingForTransaction) {
      navigate("/my-inventory");
    } else if (currentPhase < 6) {
      setCurrentPhase(currentPhase + 1);
    }
  };

  useEffect(() => {
    if (metadataHash && !isMetadataPending) {
      setIsSubmitting(false);
      setCurrentPhase(7);
      setError(null);
    } else if (metadataError) {
      setIsSubmitting(false);
      setError("Transaction failed. Please try again.");
    }
  }, [metadataHash, isMetadataPending, metadataError]);

  const phaseContent = getPhaseContent();

  const renderInput = () => {
    if (phaseContent.isPreview) {
      return (
        <div className="preview-container">
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label d-block small opacity-75 font-mono ps-3">
              Name
            </label>
            <div className="form-control no-transform">{brainName}</div>
          </div>
          <div className="mb-3">
            <label className="form-label d-block small opacity-75 font-mono ps-3">
              Ticker
            </label>
            <div className="form-control no-transform">${brainTicker}</div>
          </div>
          <div className="mb-3">
            <label className="form-label d-block small opacity-75 font-mono ps-3">
              Domain
            </label>
            <div className="form-control no-transform">{brainDomain}</div>
          </div>
          {brainImage && (
            <div className="mb-3 text-center">
              <label className="form-label d-block small opacity-75 font-mono">
                Logo
              </label>
              <img
                src={brainImage}
                alt="Brain Logo"
                style={{
                  maxWidth: "100px",
                  maxHeight: "100px",
                  margin: "auto",
                  display: "block",
                }}
              />
            </div>
          )}
          {currentPhase === 7 &&
            !isWaitingForTransaction &&
            isTransactionSuccess && (
              <div className="text-center mt-4">
                <small className="font-mono" style={{ color: "#75FBFD" }}>
                  Your Brain's metadata has been updated
                </small>
              </div>
            )}
        </div>
      );
    }

    if (phaseContent.isDropdown) {
      return (
        <div className="position-relative">
          <div className="position-relative">
            <select
              className="form-control pe-5"
              value={phaseContent.value}
              onChange={phaseContent.onChange}
              disabled={loading}
            >
              <option value="">Select one of your Brains</option>
              {ownedBrains.map((brain) => (
                <option key={brain.tokenId} value={brain.tokenId}>
                  Brain {brain.tokenId} -{" "}
                  {brain.metadata.name || "Unnamed Brain"}
                </option>
              ))}
            </select>
            <div
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <svg
                width="19"
                height="20"
                viewBox="0 0 19 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.70605 7.64746L9.41194 12.3533L14.1178 7.64746"
                  stroke="#75FBFD"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="position-relative">
        <input
          type="text"
          className="form-control no-transform"
          value={phaseContent.value}
          onChange={phaseContent.onChange}
          placeholder={phaseContent.placeholder}
        />
      </div>
    );
  };

  return (
    <>
      <ButtonHoverAnimation />
      <main data-barba="container" data-barba-namespace="secondary">
        <div className="min-svh-100 d-flex align-items-center justify-content-center px-3 px-sm-4 px-md-5 opacity-load">
          <div className="container w-100">
            <div className="spacer"></div>
            <div className="my-4">
              <h1 className="text-center">Configure Metadata</h1>
              <div className="mt-4 mt-lg-5">
                <div className="box mx-auto">
                  <div className="box-head py-3 py-md-4 px-3 px-md-5 position-relative">
                    <button
                      onClick={() => setCurrentPhase(currentPhase - 1)}
                      disabled={currentPhase === 1}
                      className="bg-transparent border-0 p-0"
                      style={{
                        cursor: currentPhase === 1 ? "default" : "pointer",
                        position: "absolute",
                        left: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "24px",
                        display: currentPhase === 1 ? "none" : "",
                      }}
                    >
                      <svg
                        width="21"
                        height="20"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.7646 5.29395L8.05877 9.99983L12.7646 14.7057"
                          stroke={currentPhase === 1 ? "#ffff" : "#75FBFD"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <p
                      className="font-mono text-uppercase d-block mb-0 text-center w-100"
                      style={{
                        color: currentPhase === 7 ? "#75FBFD" : "inherit",
                      }}
                    >
                      {phaseContent.headerTitle}
                    </p>
                  </div>
                  <hr className="my-0 border-green" />
                  <div className="pt-3 pt-md-4 px-3 px-md-5">
                    <form onSubmit={handleNext} className="metadata-form">
                      <div className="form">
                        <div className="row">
                          <div className="col-sm-12 mb-3">{renderInput()}</div>
                        </div>
                        <button
                          type="submit"
                          disabled={phaseContent.isDisabled || loading}
                        >
                          {loading ? <SvgSpinner /> : phaseContent.buttonText}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="pb-3 pb-md-4 px-3 px-md-5">
                    {!isMobile && (
                      <div className="readMoreContent read-more-content-mobile">
                        <hr className="my-0 my-sm-4" />
                        <small className="font-mono muted text-uppercase d-block">
                          Configure your Brain's metadata. Your Brain's metadata
                          is it's branding and will be displayed when users are
                          browsing through the Brains on the network.
                        </small>
                      </div>
                    )}
                    <div
                      className={`readMore d-sm-none ${
                        isVisible ? "open" : ""
                      }`}
                      onClick={toggleVisibility}
                    >
                      <strong>
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12.0068"
                            cy="12.9766"
                            r="12"
                            transform="rotate(90 12.0068 12.9766)"
                            fill="#75FBFD"
                          />
                          <path
                            d="M15.249 11.8496L12.0073 14.1041L8.76562 11.8496"
                            stroke="black"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </strong>
                      <span className="ps-1">
                        {isVisible ? "Close" : "Read More"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <Link
                className="underline-on-hover-2 d-flex align-items-center mx-auto mt-4 justify-content-center"
                to="/acquire-brain"
              >
                <em>
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12.4526" cy="12.3848" r="12" fill="#75FBFD" />
                    <path
                      d="M13.7493 15.6265L11.1559 12.3848L13.7493 9.14306"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </em>
                <span className="ps-2">Go Back</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Metadata;
